import { useQuery } from '@tanstack/react-query';
import { ApiError } from '../../lib/api';
import {
  getInfoCards,
  getInfoCardStacks,
  getInfoCardAudios,
  getInfoCardStack,
} from '../../services/backendService';
import { InfoCard, InfoCardStack, InfoCardAudio } from '../../types/cards.types';
import { SupportedLanguage } from 'lib/i18n';
import { ChatbotConfig } from 'types/chatbotConfig.types';

/**
 * Maps legacy UI category names to granular topic IDs.
 * The Welcome page shows 7 user-friendly categories, but the backend uses
 * granular topic IDs for more precise topic detection and info card matching.
 */
export type LegacyCategory =
  | 'badFeeling'
  | 'confused'
  | 'eating'
  | 'identity'
  | 'relationships'
  | 'suicidal'
  | 'violenceCrisis'
  | 'domesticViolenceForms'
  | 'abusiveRelationshipInfo'
  | 'criminalProcessGuidance'
  | 'leavingAbusiveRelationship'
  | 'safetyAndRiskAssessment'
  | 'supportServices';

export const legacyCategoryToTopics: Record<LegacyCategory, string[]> = {
  badFeeling: [
    'whatIsAnxiety',
    'doIHaveDepression',
    'whyDoIFeelBad',
    'whatIsPanicAttack',
    'whySelfHarmIsAddictive',
  ],
  confused: ['whyDoIFeelConfused', 'dailyChallengesAndWellbeing', 'addictionAndSubstances'],
  eating: [
    'eatingChallenges',
    'whatIsEatingDisorder',
    'bodyImageConcerns',
    'healthyRelationshipWithFood',
  ],
  identity: ['whatIsIdentity', 'understandingSexuality', 'understandingGender', 'futureAnxiety'],
  relationships: [
    'loneliness',
    'relationshipsAndBreakups',
    'recognizingGoodFriends',
    'isMyHomeNormal',
  ],
  suicidal: ['suicidalThoughts'],
  violenceCrisis: ['personalBoundaries', 'ifIUseViolence', 'formsOfViolence', 'amIInCrisis'],
  // RIKU categories (big topics -> granular topic IDs)
  domesticViolenceForms: [
    'psychologicalDomesticViolence',
    'physicalDomesticViolence',
    'sexualDomesticViolence',
    'digitalDomesticViolence',
    'economicDomesticViolence',
    'spiritualDomesticViolence',
    'honorBasedDomesticViolence',
    'coerciveControlInRelationship',
    'postSeparationViolenceAndStalking',
  ],
  abusiveRelationshipInfo: [
    'whatIsDomesticViolence',
    'differenceBetweenArguingAndViolence',
    'abusiveRelationshipCycleAndDynamics',
    'warningSignsOfAbusiveRelationship',
    'effectsOfDomesticViolenceOnVictim',
    'domesticViolenceExperiencedByChildrenAndYouth',
    'domesticViolenceCrisisAndTrauma',
    'acuteStressResponseFightFlightFreezeFawn',
  ],
  criminalProcessGuidance: [
    'criminalProcessOverview',
    'collectingEvidence',
    'restrainingOrder',
    'victimRightsInCriminalProcess',
  ],
  leavingAbusiveRelationship: [
    'whyLeavingAbusiveRelationshipIsHard',
    'divorceAndSeparation',
    'housingArrangementsWhenLeavingAbusiveRelationship',
    'dividingPropertyAfterSeparation',
    'childCustodyAndVisitationAfterSeparation',
    'supportingRecoveryAfterDomesticViolence',
  ],
  safetyAndRiskAssessment: [
    'protectingFromViolenceDuringAndAfterRelationship',
    'digitalSafetyInRelationships',
    'safetyPlan',
    'supportingVictimAndTalkingAboutViolence',
  ],
  supportServices: ['supportServicesForDomesticViolenceVictims'],
};

const isLegacyCategory = (category: string): category is LegacyCategory =>
  category in legacyCategoryToTopics;

export const useInfoCardStacks = (language: SupportedLanguage, config?: ChatbotConfig) => {
  return useQuery<
    {
      categories: string[];
      stacksByCategory: Record<string, InfoCardStack[]>;
      allStacks: InfoCardStack[];
      getStacksForCategory: (category: string) => InfoCardStack[];
    },
    ApiError
  >({
    queryKey: ['infoCardStacks', language, config?.infoCardTopics],
    queryFn: async () => {
      const categories = config?.infoCardTopics ?? [];
      const stacks = await getInfoCardStacks({ language });

      const stacksByCategory: Record<string, InfoCardStack[]> = {};
      for (const category of categories) {
        const granular = isLegacyCategory(category) ? legacyCategoryToTopics[category] : [category];
        const granularSet = new Set(granular);
        stacksByCategory[category] = stacks.filter((stack) => granularSet.has(stack.topicId));
      }

      const getStacksForCategory = (category: string) => stacksByCategory[category] ?? [];

      return { categories, stacksByCategory, allStacks: stacks, getStacksForCategory };
    },
    enabled: !!config,
  });
};

export const useInfoCardStack = (language: SupportedLanguage, stackId?: string) => {
  return useQuery<InfoCardStack, ApiError>({
    queryKey: ['infoCardStack', stackId],
    queryFn: () => getInfoCardStack({ stackId: stackId!, language }),
    enabled: !!stackId,
  });
};

export const useInfoCards = (language: SupportedLanguage, stackId?: string) => {
  return useQuery<InfoCard[], ApiError>({
    queryKey: ['infoCards', language, stackId],
    queryFn: () => getInfoCards({ language, stackId: stackId! }),
    enabled: !!stackId,
  });
};

export const useInfoCardAudio = (language: SupportedLanguage, stackId: string) => {
  return useQuery<InfoCardAudio | null, ApiError>({
    queryKey: ['infoCardAudio', language, stackId],
    queryFn: async () => {
      const [audio] = await getInfoCardAudios({ language, stackId });
      return audio || null;
    },
  });
};

export const useInfoCardAudioAvailability = (language: SupportedLanguage, stackIds: string[]) => {
  return useQuery<boolean, ApiError>({
    queryKey: ['infoCardAudioAvailability', language, stackIds],
    queryFn: async () => {
      if (stackIds.length === 0) {
        return false;
      }

      for (const stackId of stackIds) {
        const audios = await getInfoCardAudios({ language, stackId });
        if (audios.some((audio) => Boolean(audio.url))) {
          return true;
        }
      }

      return false;
    },
    enabled: stackIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });
};
